import { useMsal } from "@azure/msal-react";
import { useState } from "react";
import PPLogo from "../components/common/PPLogo";
import { loginRequest } from "./msalConfig";
import { useGuest } from "./GuestContext";

// ═══════════════════════════════════════════════════════════════════════════════
// LOGIN SCREEN
// ═══════════════════════════════════════════════════════════════════════════════
// Two paths:
//   1. "Sign in with Microsoft" — Entra ID redirect, restricted to PPUSA tenant.
//   2. "Continue as guest" — shared username/password (validated in GuestContext)
//      that drops the user into a read-only Pipeline + Map session for outside
//      viewers.
//
// Light theme on purpose — the red-on-white brand lockup is the canonical
// presentation, and the in-app dark mode is a per-user preference that
// hasn't loaded yet.

export default function LoginScreen() {
  const { instance } = useMsal();
  const { signIn: signInGuest } = useGuest();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("microsoft"); // "microsoft" | "guest"
  const [guestUsername, setGuestUsername] = useState("");
  const [guestPassword, setGuestPassword] = useState("");
  const [guestError, setGuestError] = useState(null);

  async function handleSignIn() {
    setError(null);
    setLoading(true);
    try {
      // Full-page redirect — simpler and more reliable than popup, and avoids
      // the popup-blank / nested-popup edge cases. Auth response is processed
      // by handleRedirectPromise() in index.js when the page loads back.
      await instance.loginRedirect(loginRequest);
    } catch (e) {
      setError(e?.errorMessage || e?.message || "Sign in failed. Please try again.");
      setLoading(false);
    }
  }

  function handleGuestSignIn(e) {
    e.preventDefault();
    setGuestError(null);
    if (signInGuest(guestUsername, guestPassword)) return;
    setGuestError("Invalid guest credentials.");
  }

  const guestInputCls =
    "w-full text-sm px-3 py-2.5 rounded-lg border border-pp-gray bg-white text-pp-dark placeholder-[#7a7a7a] focus:outline-none focus:border-pp-red focus:ring-1 focus:ring-pp-red transition-colors";

  return (
    <div className="min-h-screen font-montserrat flex items-center justify-center px-6" style={{ backgroundColor: "#f4f4f4", color: "#333333" }}>
      <div className="w-full max-w-md">
        {/* Brand lockup */}
        <div className="flex flex-col items-center mb-10">
          <PPLogo height={56} variant="red" />
          <span className="mt-3 text-[11px] font-bold tracking-[0.25em] uppercase text-[#8a8a8a]">PROXY</span>
        </div>

        {/* Card */}
        <div className="bg-white border border-pp-gray rounded-2xl p-8 shadow-lg">
          {mode === "microsoft" ? (
            <>
              <h1 className="text-xl font-bold mb-2 text-center text-pp-dark">Sign in to continue</h1>
              <p className="text-sm text-[#8a8a8a] text-center mb-8">
                Use your <span className="text-pp-dark font-semibold">@proximityparks.com</span> account to access Proxy.
              </p>

              <button
                onClick={handleSignIn}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 bg-pp-red hover:bg-pp-red-light disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm px-5 py-3 rounded-lg transition-colors"
              >
                {/* Microsoft logo */}
                <svg width="18" height="18" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <rect x="1" y="1" width="10" height="10" fill="#f25022" />
                  <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
                  <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
                  <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
                </svg>
                <span style={{ color: "#ffffff" }}>{loading ? "Signing in…" : "Sign in with Microsoft"}</span>
              </button>

              {error && (
                <div className="mt-4 text-sm text-[#a5241b] bg-[rgba(233,53,41,0.07)] border border-[rgba(233,53,41,0.25)] rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <div className="mt-6 flex items-center gap-3">
                <div className="flex-1 h-px bg-pp-gray" />
                <span className="text-[10px] font-bold tracking-widest uppercase text-[#8a8a8a]">or</span>
                <div className="flex-1 h-px bg-pp-gray" />
              </div>

              <button
                onClick={() => { setMode("guest"); setError(null); }}
                className="mt-4 w-full text-sm font-semibold text-pp-dark hover:bg-[#f4f4f4] border border-pp-gray rounded-lg px-5 py-3 transition-colors bg-white"
              >
                Continue as guest
              </button>
            </>
          ) : (
            <>
              <h1 className="text-xl font-bold mb-2 text-center text-pp-dark">Guest access</h1>
              <p className="text-sm text-[#8a8a8a] text-center mb-6">
                View-only access to Pipeline and Map.
              </p>

              <form onSubmit={handleGuestSignIn} className="space-y-3">
                <input
                  type="text"
                  placeholder="Username"
                  value={guestUsername}
                  onChange={(e) => setGuestUsername(e.target.value)}
                  className={guestInputCls}
                  autoFocus
                  autoComplete="username"
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={guestPassword}
                  onChange={(e) => setGuestPassword(e.target.value)}
                  className={guestInputCls}
                  autoComplete="current-password"
                />
                <button
                  type="submit"
                  className="w-full bg-pp-red hover:bg-pp-red-light text-white font-semibold text-sm px-5 py-3 rounded-lg transition-colors"
                  style={{ color: "#ffffff" }}
                >
                  Continue
                </button>
              </form>

              {guestError && (
                <div className="mt-4 text-sm text-[#a5241b] bg-[rgba(233,53,41,0.07)] border border-[rgba(233,53,41,0.25)] rounded-lg px-3 py-2">
                  {guestError}
                </div>
              )}

              <button
                onClick={() => { setMode("microsoft"); setGuestError(null); setGuestPassword(""); }}
                className="mt-4 w-full text-xs font-semibold text-[#8a8a8a] hover:text-pp-dark transition-colors"
              >
                ← Back to Microsoft sign-in
              </button>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-[#8a8a8a]">
          Access restricted to ProximityParks USA employees and approved guests.
        </p>
      </div>
    </div>
  );
}
